export const MIN_REQUIRED_FAVORITES = 3;
export const MIN_REQUIRED_SIGNALS = 2;
/** Temporarily 3 steps — popular-searches (step 4) is commented out in the wizard/modal. */
export const TOTAL_ONBOARDING_STEPS = 3;
/** Bump when step order or flow structure changes (used to migrate saved progress). */
export const ONBOARDING_FLOW_VERSION = 4;
export const ONBOARDING_FLOW_HEADING = 'Personalizing your workspace';

/** Shared modal shell — fixed height so step footers do not jump. */
export const ONBOARDING_MODAL_SHELL_CLASS =
  'flex h-[93dvh] max-h-[93dvh] w-full flex-col overflow-hidden bg-white sm:h-[min(700px,90dvh)] sm:max-h-[min(700px,90dvh)]';

/** Scrollable step content — scrollbar appears only when overflow exists. */
export const ONBOARDING_SCROLL_AREA_CLASS =
  'onboarding-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain';

/** Centered analysis insight box shown at the bottom of selection steps. */
export const ONBOARDING_INSIGHT_PANEL_CLASS =
  'relative mx-auto w-full max-w-md overflow-hidden rounded-2xl border border-emerald-200/70 bg-gradient-to-br from-emerald-50/95 via-white to-teal-50/80 px-3 py-3 shadow-[0_4px_14px_-8px_rgba(16,185,129,0.22)] ring-1 ring-inset ring-white/70 sm:px-5 sm:py-3.5';

/** Neutral panel surface used while an insight is still analyzing. */
export const ONBOARDING_INSIGHT_PANEL_ANALYZING_CLASS =
  'relative mx-auto w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white px-4 py-3 shadow-[0_4px_14px_-8px_rgba(15,23,42,0.08)] ring-1 ring-inset ring-gray-50 sm:px-5 sm:py-3.5';

/** Progress track shown while an insight panel is analyzing selections. */
export const ONBOARDING_ANALYZING_PROGRESS_TRACK_CLASS =
  'relative mt-3 h-1.5 overflow-hidden rounded-full bg-emerald-100/90 shadow-inner';

/** How long each analyzing message stays on screen. */
export const ONBOARDING_ANALYZING_MESSAGE_INTERVAL_MS = 1000;

/** Analyzing runs long enough for this many messages to be read. */
export const ONBOARDING_ANALYZING_MIN_MESSAGES = 2;

/** Wrapper for the insight panel — adds desktop spacing above the step footer. */
export const ONBOARDING_INSIGHT_SECTION_CLASS =
  'flex shrink-0 justify-center sm:mt-auto sm:pb-5';

/** Flex column wrapper for step content + optional insight panel. */
export const ONBOARDING_STEP_BODY_CLASS = 'flex min-h-0 flex-1 flex-col gap-3 sm:gap-4';

export const STEP_COPY = {
  investingStyle: {
    headline: 'What kinds of businesses interest you?',
    subtext: 'This helps us understand how you tend to evaluate opportunities.',
    continueCta: 'Continue',
    matchResultLabel: 'companies matching your styles',
  },
  companies: {
    headline: 'What companies are you following?',
    subtext: 'Add companies you own or are seriously researching.',
    searchPlaceholder: 'Search ticker or company',
    emptyHint: 'No companies added yet',
    continueCta: 'Continue',
    minCompaniesError: 'Please add at least 3 companies.',
    companyAddedToast: (tickerSymbol: string) => `${tickerSymbol} added successfully`,
    dnaMatchLabel: 'companies with similar stats',
    savedPatternsLabel: (count: number) =>
      `company ${count === 1 ? 'pattern' : 'patterns'}`,
    adjustPatternsNote: 'Refine these anytime',
    benefitPointersHeading: 'Helps with',
    benefitPointers: [
      'Personalizing the experience around your companies and investing style.'
    ],
  },
  signals: {
    headline: "Select fundamentals you care about",
    subtext:
      "Tell us what matters most to you?",
    selectAllLabel: 'Select All',
    selectAllRecommended: 'Recommended',
    selectAllHint: 'Covers core fundamentals in one tap.',
    continueCta: 'Continue',
    minSignalsError: 'Please select at least two fundamentals.',
    matchResultLabel: 'company matches',
  },
  themes: {
    headline: 'Pick Your Areas of Interest',
    subtext: 'Discover companies and trends tailored to your interests.',
    continueCta: 'Continue',
  },
  popularSearches: {
    headline: 'Pick from our popular searches',
    subtext: "We will save these for you to run quick screens",
    selectAllLabel: 'Select All',
    selectAllRecommended: 'Recommended',
    selectAllHint: 'Save every starter screener in one tap.',
    continueCta: 'Continue to Dashboard',
    coverageResultLabel: 'companies covered',
    savedScreenersLabel: (count: number) =>
      `starter ${count === 1 ? 'screener' : 'screeners'}`,
    adjustScreenersNote: 'Adjust your screens anytime',
  },
} as const;

export const ANALYSIS_MESSAGES = {
  investingStyle: [
    'Analyzing thousands of companies...',
    'Applying your style filters...',
    'Finding your best matches...',
  ],
  companies: [
    'Analyzing your selections...',
    'Identifying investing patterns...',
    'Finding similar companies...',
  ],
  signals: [
    'Analyzing 4,000+ companies...',
    'Ranking opportunities...',
    'Finding your best matches...',
  ],
  popularSearches: [
    'Analyzing starter screeners...',
    'Calculating company coverage...',
    'Personalizing your experience...',
  ],
} as const;

export const COMPLETION_COPY = {
  title: 'Personalizing Your Space...',
  items: [
    'Applying your investing style filters',
    'Analyzing your favorite companies',
    'Building your personalized screeners',
    'Personalizing your news feed',
  ],
} as const;

export const ONBOARDING_COMPLETION_ITEM_STAGGER_MS = 400;
export const ONBOARDING_COMPLETION_POST_ANIMATION_MS = 700;

export function getOnboardingCompletionDurationMs(): number {
  return (
    COMPLETION_COPY.items.length * ONBOARDING_COMPLETION_ITEM_STAGGER_MS +
    ONBOARDING_COMPLETION_POST_ANIMATION_MS
  );
}
